import React, { useEffect, useState } from 'react';
import { AuthImage } from '../Offers/DrawingPairModal';
import './DocumentKindStep.css';

/** LAKPED-16.1 — lo primero que se pregunta al subir un documento: ¿petición de oferta o pedido?
 *
 *  Mientras el usuario lo decide, el documento se va leyendo por detrás y los campos de la derecha
 *  se rellenan solos. Lo que viene de la IA se pinta sobre fondo azul claro y se puede corregir
 *  aquí mismo: la corrección viaja a la pantalla siguiente, sea oferta o pedido.
 */
const PASOS = ['Documento subido', '¿Oferta o pedido?', 'Histórico', 'Datos'];

const DocumentKindStep = ({
  file,
  escaneo,
  escaneando,
  error,
  onOferta,
  onPedido,
  onCambiar,
}) => {
  const [vistaPrevia, setVistaPrevia] = useState(null);
  const [datos, setDatos] = useState({ cliente: '', articulo: '', numero_documento: '' });
  const [detectado, setDetectado] = useState({});

  useEffect(() => {
    if (!file) return undefined;
    const url = URL.createObjectURL(file);
    setVistaPrevia({ url, esPdf: /pdf$/i.test(file.type) || /\.pdf$/i.test(file.name) });
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    const leido = escaneo?.order_data;
    if (!leido) return;
    setDatos({
      cliente: leido.cliente || '',
      articulo: leido.articulo || '',
      numero_documento: leido.numero_documento || '',
    });
    setDetectado({
      cliente: Boolean(leido.cliente),
      articulo: Boolean(leido.articulo),
      numero_documento: Boolean(leido.numero_documento),
    });
  }, [escaneo]);

  // LAKPED-30: en cuanto el documento está leído se pintan TODAS sus páginas renderizadas por el
  // servidor, apiladas con scroll y con zoom, en vez de una sola página fija.
  const paginaPlano = (escaneo?.drawing_pages || [])[0];
  const totalPaginas = escaneo?.analysis_hash ? Math.max(escaneo.total_pages || 0, paginaPlano || 0, 1) : 0;
  const [zoom, setZoom] = useState(1);
  const ZOOM_PASO = 0.25;
  const ZOOM_MAX = 3;

  const cambiar = (campo) => (e) => setDatos(prev => ({ ...prev, [campo]: e.target.value }));
  const paginas = escaneo?.total_pages;
  const listo = Boolean(escaneo?.analysis_hash);

  const campo = (clave, etiqueta, pista) => (
    <>
      <div className={`document-kind-campo${detectado[clave] ? ' detectado' : ''}`}>
        <label htmlFor={`doc-${clave}`}>
          {etiqueta}
          {detectado[clave] && <em className="document-kind-badge">detectado</em>}
        </label>
        <input id={`doc-${clave}`} value={datos[clave]} onChange={cambiar(clave)} placeholder="—" />
      </div>
      {pista && <p className="document-kind-pista">{pista}</p>}
    </>
  );

  return (
    <div className="document-kind">
      <ol className="document-kind-pasos">
        {PASOS.map((titulo, i) => (
          <li key={titulo} className={i === 1 ? 'actual' : (i === 0 ? 'hecho' : '')}>
            <span className="document-kind-paso-num">{i === 0 ? '✓' : i + 1}</span>
            {titulo}
          </li>
        ))}
      </ol>

      <h1>¿Qué es este documento?</h1>
      <p className="document-kind-fichero">{file?.name} · recién subido</p>

      <div className="document-kind-columnas">
        <div className="document-kind-previa">
          {totalPaginas > 0 ? (
            <div className="document-kind-paginas">
              {Array.from({ length: totalPaginas }, (_, i) => i + 1).map(pagina => (
                <figure key={pagina} className={pagina === paginaPlano ? 'plano' : ''} style={{ width: `${zoom * 100}%` }}>
                  <AuthImage analysisHash={escaneo.analysis_hash} page={pagina} alt={`Página ${pagina}`} />
                  <figcaption>Página {pagina}{pagina === paginaPlano ? ' · plano' : ''}</figcaption>
                </figure>
              ))}
            </div>
          ) : vistaPrevia?.esPdf ? (
            <embed src={`${vistaPrevia.url}#toolbar=0&navpanes=0`} type="application/pdf" title="Vista previa" />
          ) : vistaPrevia ? (
            <img src={vistaPrevia.url} alt="Vista previa del documento" />
          ) : (
            <div className="document-kind-previa-vacia">Sin vista previa</div>
          )}
          <span className="document-kind-previa-pie">
            <span>
              Vista previa{paginas ? ` · ${paginas} ${paginas === 1 ? 'página' : 'páginas'}` : ''}
              {paginaPlano ? ` · plano en la página ${paginaPlano}` : ''}
            </span>
            {totalPaginas > 0 && (
              <span className="document-kind-zoom">
                <button type="button" onClick={() => setZoom(z => Math.max(1, z - ZOOM_PASO))} disabled={zoom <= 1} title="Alejar">−</button>
                <b>{Math.round(zoom * 100)}%</b>
                <button type="button" onClick={() => setZoom(z => Math.min(ZOOM_MAX, z + ZOOM_PASO))} disabled={zoom >= ZOOM_MAX} title="Acercar">+</button>
              </span>
            )}
          </span>
        </div>

        <div>
          <div className="document-kind-tarjeta">
            <h2>Lo que se ha detectado</h2>

            {escaneando && (
              <p className="document-kind-leyendo">Leyendo el documento. Tarda unos segundos.</p>
            )}
            {error && <p className="document-kind-error">{error}</p>}

            {campo('cliente', 'Cliente propuesto', 'Se puede corregir aquí; el cliente definitivo se elige en la pantalla siguiente.')}
            {campo('articulo', 'Código de pieza / artículo')}
            {campo('numero_documento', 'Nº de documento del cliente', 'Lo que viene sobre fondo azul lo ha leído la IA del documento.')}
          </div>

          <div className="document-kind-tarjeta">
            <h2>¿Qué es?</h2>
            <div className="document-kind-opciones">
              <button
                type="button"
                className="document-kind-opcion"
                onClick={() => onOferta(datos)}
                disabled={!listo}
              >
                <b>Petición de oferta</b>
                <span>Quieren precio. Buscamos el histórico del artículo y montamos la oferta.</span>
              </button>
              <button
                type="button"
                className="document-kind-opcion"
                onClick={() => onPedido(datos)}
                disabled={!listo}
              >
                <b>Pedido</b>
                <span>Ya lo han pedido. Se registra, se busca la oferta relacionada y se empiezan a apuntar partes.</span>
              </button>
            </div>
          </div>

          <div className="document-kind-pie">
            <button type="button" className="document-kind-secundario" onClick={onCambiar}>
              Cambiar documento
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentKindStep;
